import React from 'react';
import { Calendar, ArrowRight, Trash2, Pencil } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const TemplateCard = ({ template, previewImage, onDelete, variant = 'standard', onClick }) => {
  const navigate = useNavigate();

  const handleClick = () => {
    if (onClick) {
      onClick(template);
      return;
    }
    // Navigate to builder with template ID in state
    navigate(`/template/builder/${template.id}`);
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this template?')) {
      onDelete(template.id);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const isSimple = variant === 'simple';

  return (
    <div
      onClick={handleClick}
      className={`group relative overflow-hidden rounded-2xl bg-white cursor-pointer transition-all duration-300 hover:scale-105 hover:shadow-2xl border border-gray-100 ${isSimple ? 'aspect-[4/5]' : ''}`}
      style={{ aspectRatio: isSimple ? 'auto' : '4/3' }}
    >
      {/* Preview Image */}
      <div className={`relative ${isSimple ? 'h-full' : 'h-3/4'} overflow-hidden`}>
        {previewImage || template.preview_url ? (
          <img
            src={previewImage || template.preview_url}
            alt={template.name}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
          />
        ) : (
          <div 
            className="w-full h-full flex items-center justify-center relative"
            style={{
              background: template.id === 'sample-template-1' 
                ? 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)'
                : 'linear-gradient(135deg, #f093fb 0%, #f5576c 50%, #4facfe 100%)'
            }}
          >
            {/* Decorative circles */}
            <div className="absolute inset-0 overflow-hidden">
              <div className="absolute top-10 left-10 w-32 h-32 bg-white/10 rounded-full blur-2xl" />
              <div className="absolute bottom-10 right-10 w-40 h-40 bg-white/10 rounded-full blur-3xl" />
            </div>
            
            {/* Template name initial - Only show if not simple or visual fallback needed */}
            <div className="relative z-10 text-white">
              <div className="text-8xl font-bold opacity-90 mb-4">
                {template.name.charAt(0)}
              </div>
              {!isSimple && (
                  <div className="text-center text-sm font-medium opacity-75">
                    {template.name}
                  </div>
              )}
            </div>
          </div>
        )}

        {/* Delete Button - Hide in simple mode if not needed, or keep */}
        {!isSimple && (
            <div 
            onClick={handleDelete}
            className="absolute top-3 right-3 p-2 bg-white/90 backdrop-blur-sm rounded-full cursor-pointer hover:bg-red-50 group-hover:opacity-100 opacity-0 transition-opacity duration-300 z-20"
            title="Delete Template"
            >
            <Trash2 className="w-4 h-4 text-red-500" />
            </div>
        )}
        
        {/* Gradient overlay on hover */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        
        {/* Hover action */}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <div className="flex gap-2 transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
             {/* Use Template Button */}
             <div className="px-4 py-2 bg-white/95 backdrop-blur-sm rounded-full flex items-center gap-2 cursor-pointer hover:bg-white transition-colors"
                  onClick={(e) => {
                      e.stopPropagation(); // Prevent card click
                      handleClick();
                  }}
             >
                <span className="text-xs font-semibold text-gray-900">{isSimple || onClick ? 'Use' : 'Open'}</span>
             </div>

             {/* Edit Template Button (Icon only) */}
             <div 
                className="p-2 bg-white/95 backdrop-blur-sm rounded-full flex items-center justify-center cursor-pointer hover:bg-white transition-colors"
                title="Edit Template Source"
                onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/template/builder/${template.id}`);
                }}
             >
                <Pencil className="w-4 h-4 text-[#8E0DFF]" />
             </div>
          </div>
        </div>
      </div>
      
      {/* Info Section - Hide in simple mode */}
      {!isSimple && (
          <div className="h-1/4 p-4 flex flex-col justify-between bg-white">
            <h3 className="text-base font-bold text-gray-900 truncate group-hover:text-[#8E0DFF] transition-colors duration-300">
              {template.name}
            </h3>
            
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <Calendar className="w-3 h-3" />
              <span>{formatDate(template.created_at)}</span>
              <span className="text-gray-300">•</span>
              <span>#{template.id}</span>
            </div>
          </div>
      )}
      
      {/* Accent border on hover */}
      <div className="absolute inset-0 border-2 border-[#8E0DFF] rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
    </div>
  );
};

export default TemplateCard;
